import { type MaskOptions, useMask } from '@react-input/mask';
import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  useId,
  useRef,
} from 'react';
import type { FieldValues, Path, UseFormRegister } from 'react-hook-form';
import { TransitionGroup } from 'react-transition-group';

import InputErrorText from '@/components/InputErrorText/InputErrorText';
import { trimPhone } from '@/utils/phone';
import { maskInputChangeEvent } from '@/utils/utils';

import useInputStyles from './FormInput.styles';

export interface InputProps<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  name: Path<T>;
  label?: string;
  setValue?: (value: string) => void;
  error?: string;
  isOnDark?: boolean;
  type?: InputType;
  testId?: string;
  subtext?: string;
  inputSize?: InputSize;
  register?: UseFormRegister<T>;
  readOnly?: boolean;
}
export enum InputSize {
  SM = 'sm',
  LG = 'lg',
}

export enum InputType {
  TEXT = 'text',
  TEL = 'tel',
  DATE = 'date',
  NUMBER = 'number',
  DOLLAR = 'dollar',
  ZIP = 'zip',
  // HONEYPOT FIELD
  ADDRESS = 'address',
}

interface InputRowProps {
  children: React.ReactNode;
  classes?: string;
}

/**
 * @deprecated use /components/InputRow
 */
export const InputRow = ({ children, classes }: InputRowProps) => {
  return (
    <div className={classes} style={{ width: '100%', display: 'flex' }}>
      {children}
    </div>
  );
};

export const getMaskByType = (inputType: InputType): MaskOptions => {
  const replacement = { _: /\d/ };

  if (inputType === InputType.TEL) {
    return { mask: '(___) ___-____', replacement };
  }
  if (inputType === InputType.DATE) {
    return { mask: '__/__/____', replacement };
  }
  if (inputType === InputType.ZIP) {
    return { mask: '_____', replacement };
  }
  return {};
};

const FormInput = <T extends FieldValues>({
  name,
  label,
  value,
  setValue,
  error,
  isOnDark = false,
  type = InputType.TEXT,
  onBlur,
  onFocus,
  onChange,
  testId,
  placeholder,
  subtext,
  register,
  inputSize = InputSize.LG,
  readOnly,
  ...rest
}: InputProps<T>) => {
  const { classes, cx } = useInputStyles({ isOnDark });
  const inputName = `${name}-${useId()}`;

  const getRegister = (
    register: UseFormRegister<T> | undefined,
    type: InputType,
    name: Path<T>
  ) => {
    if (!register) {
      return null;
    }

    if (type === InputType.DOLLAR) {
      return register(name, {
        setValueAs: (v: string) => v.replace('$', ''),
      });
    }

    if (type === InputType.TEL) {
      return register(name, {
        setValueAs: (v: string) => trimPhone(v),
      });
    }

    return register(name);
  };
  const registerValues = getRegister(register, type, name);

  const maskRef = useMask(getMaskByType(type));
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const maskedEvent = maskInputChangeEvent(event, type);
    if (onChange) onChange(maskedEvent);

    if (setValue) {
      setValue(maskedEvent.target.value);
    }
    if (registerValues) {
      registerValues.onChange(maskedEvent);
    }
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (onBlur) onBlur(event);
    if (registerValues) registerValues.onBlur(event);
  };
  const inputRef = (element: HTMLInputElement) => {
    if (registerValues) {
      registerValues.ref(element);
    }
    maskRef.current = element;
  };

  const maskedInputTypes: InputType[] = [
    InputType.TEL,
    InputType.DATE,
    InputType.ZIP,
  ];

  const hasError = !!error && error.length > 0;
  const errorId = `${name}error`;
  const errorMessageRef = useRef<HTMLSpanElement>(null);
  const sharedInputProps = {
    name: inputName,
    onBlur,
    onFocus,
    placeholder,
    value,
    id: inputName,
    ...(readOnly && { readOnly: true, 'aria-readonly': true }),
    'aria-label': label || name,
    'data-testid': testId,
    'aria-invalid': hasError ? ('true' as const) : ('false' as const),
    'aria-errormessage': errorId,
  };
  const inputClasses = cx(classes.input, classes[inputSize], {
    [classes.inputFocusActivity]: !hasError,
    [classes.inputHoverActivity]: !hasError && !readOnly,
    [classes.inputError]: hasError,
  });

  return (
    <div className={classes.wrapper}>
      <div className={classes.inputContainer}>
        <label htmlFor={inputName} className={classes.inputLabel}>
          {label}
        </label>
        {maskedInputTypes.includes(type) ? (
          <input
            className={inputClasses}
            {...sharedInputProps}
            {...rest}
            {...(registerValues && registerValues)}
            ref={inputRef}
            onChange={handleChange}
            onBlur={handleBlur}
            type={type === InputType.ZIP ? InputType.TEXT : type}
          />
        ) : (
          <input
            className={inputClasses}
            {...sharedInputProps}
            {...rest}
            {...(registerValues && registerValues)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
            type={type === InputType.NUMBER ? InputType.TEXT : type}
          />
        )}
      </div>
      {(hasError || subtext) && (
        <div className={classes.bottomTextContainer}>
          <TransitionGroup>
            {hasError ? (
              <InputErrorText
                errorId={errorId}
                errorMessageRef={errorMessageRef}
                errorMessage={error}
              />
            ) : (
              subtext && (
                // biome-ignore lint/a11y/noLabelWithoutControl: Automated migration to Biome
                <label className={cx(classes.helperText, classes.subtext)}>
                  {subtext}
                </label>
              )
            )}
          </TransitionGroup>
        </div>
      )}
    </div>
  );
};

export default FormInput;
